import React, { useEffect, useState } from "react";
import { FormField } from "../inputField/textfield";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddButton } from "../button/button";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { getVendorList } from "../../redux/slices/vendorList";
import { useParams, useLocation } from "react-router-dom";
import { getProject } from "../../redux/slices/projectSlice/addProjectSlice";
import { getReview } from "../../redux/slices/reviewerSlice";

interface VendorAllocationBodyProps {
  formDataArray: Array<{
    vendor: string;
    task_count: string;
    type: string;
    stage_id?: any;
  }>;
  setFormDataArray: React.Dispatch<
    React.SetStateAction<
      Array<{
        vendor: string;
        task_count: string;
        type: string;
        stage_id?: any;
      }>
    >
  >;
}

const VendorAllocationBody: React.FC<VendorAllocationBodyProps> = ({
  formDataArray,
  setFormDataArray,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const location = useLocation();

  const vendorList = useSelector(
    (state: RootState) => state.vendorList.vendor?.results || []
  );
  const reviewList = useSelector(
    (state: RootState) => state.reviewStage.reviewer || []
  );

  useEffect(() => {
    dispatch(getVendorList({ paginate: true, project_id: id || "" }));
    dispatch(getProject({ project_id: id || "" }));
    dispatch(getReview({ project_id: id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (location.state?.formDataArray) {
      setFormDataArray(location.state.formDataArray);
    }
  }, [location.state, setFormDataArray]);

  const handleInputChange = (
    e: { target: { name: string; value: string } },
    index: number
  ) => {
    const { name, value } = e.target;
    const newFormDataArray = [...formDataArray];
    newFormDataArray[index] = {
      ...newFormDataArray[index],
      [name]: name === "task_count" ? Number(value) : value,
    };
    setFormDataArray(newFormDataArray);
  };

  const handleSelectFieldChange = (
    event: SelectChangeEvent<string[]>,
    index: number,
    fieldName: string
  ) => {
    const {
      target: { value },
    } = event;

    const parsedValue = typeof value === "string" ? value.split(",") : value;

    setFormDataArray((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updatedItem: any = { ...item };

        if (fieldName === "type") {
          updatedItem.type = parsedValue;
          if (parsedValue.includes("TASKING")) {
            delete updatedItem.review_stage_id;
          } else {
            updatedItem.review_stage_id = parsedValue.find((val) => val !== "TASKING") || null;
          }
        } else {
          updatedItem[fieldName] = parsedValue;
        }

        return updatedItem;
      })
    );
  };

  const handleAddMore = () => {
    setFormDataArray([
      ...formDataArray,
      { vendor: "", task_count: "", type: "" },
    ]);
  };

  const handleDelete = (index: number) => {
    const newFormDataArray = formDataArray.filter((_, i) => i !== index);
    setFormDataArray(newFormDataArray);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
  };

  const getVendorPermissions = (vendorId: string) => {
    const selectedVendor = vendorList.find(
      (vendor: any) => vendor.vendor_id === vendorId
    );

    if (!selectedVendor)
      return {
        taskingAllowed: false,
        reviewingAllowed: false,
        bothAllowed: false,
      };

    const permissions = selectedVendor.permissions || {};
    const tasking =
      permissions.task_allocation_single?.[0] ||
      permissions.task_allocation_bulk?.[0];
    const reviewing =
      permissions.review_allocation_single?.[0] ||
      permissions.review_allocation_bulk?.[0];
    const both = tasking && reviewing;

    return {
      taskingAllowed: tasking,
      reviewingAllowed: reviewing,
      bothAllowed: both,
    };
  };

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <form onSubmit={handleSubmit}>
        {formDataArray.map((formData, index) => {
          const permissions = getVendorPermissions(formData.vendor);

          return (
            <div
              key={index}
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexGrow: 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "20px",
                    width: "32%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "24px",
                      textAlign: "left",
                      color: "#10383D",
                    }}
                  >
                    Select Vendor
                  </Typography>
                  <Select
                    name="vendor"
                    value={formData.vendor || ""}
                    onChange={(e: any) =>
                      handleSelectFieldChange(e, index, "vendor")
                    }
                    sx={{ width: "220px" }}
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Vendor
                    </MenuItem>
                    {vendorList
                      .map((a: any) => ({
                        ...a,
                        vendor_pseudo_name_small:
                          a.pseudo_name?.toLowerCase() ||
                          a.vendor_pseudo_name?.toLowerCase(),
                      }))
                      .filter((a) => a.vendor_id !== "")
                      .sort((a, b) =>
                        a.vendor_pseudo_name_small < b.vendor_pseudo_name_small
                          ? -1
                          : a.vendor_pseudo_name_small >
                            b.vendor_pseudo_name_small
                            ? 1
                            : 0
                      )
                      .map((vendor: any) => (
                        <MenuItem
                          key={vendor.vendor_id}
                          value={vendor.vendor_id}
                          disabled={vendor.is_blocked}
                          title={vendor.is_blocked ? "Vendor is blocked on this project" : ""}
                          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                          <span>
                            {vendor.vendor_pseudo_name ||
                              vendor.pseudo_name ||
                              vendor.vendor_name}
                          </span>

                          {vendor.is_blocked && (
                            <Chip
                              label="Blocked"
                              size="small"
                              color="error"
                              sx={{ ml: 1, fontSize: "0.7rem", height: "20px" }}
                            />
                          )}
                        </MenuItem>
                      ))}
                  </Select>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "20px",
                    width: "32%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "24px",
                      textAlign: "left",
                      color: "#10383D",
                    }}
                  >
                    Select Stages
                  </Typography>
                  <Select
                    multiple
                    name="type"
                    value={formData.type || []}
                    onChange={(e: any) => handleSelectFieldChange(e, index, "type")}
                    sx={{ width: "220px" }}
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      if (!Array.isArray(selected) || selected.length === 0) return "Select Stages";

                      const selectedNames = selected.map((id) => {
                        if (id === "TASKING") return "Tasking";

                        const match = reviewList?.results?.find((review: { id: string; }) => review.id === id);
                        return match?.name || id;
                      });

                      return selectedNames.join(", ");
                    }}
                  >
                    <MenuItem disabled value="">
                      Select Stages
                    </MenuItem>

                    {permissions.taskingAllowed && (
                      <MenuItem value="TASKING">
                        <Checkbox checked={formData.type?.includes("TASKING")} />
                        Tasking
                      </MenuItem>
                    )}

                    {reviewList?.results?.map((review: any) => (
                      <MenuItem key={review.id} value={review.id}>
                        <Checkbox checked={formData.type?.includes(review.id)} />
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {review.name}
                          </Typography>
                          <Chip
                            label={review.category || "Review"}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              fontSize: "0.75rem",
                              height: 22,
                              ml: 1,
                              mt: { xs: 0.5, sm: 0 },
                            }}
                          />
                        </Box>
                      </MenuItem>

                    ))}
                  </Select>
                </div>
                <FormField
                  label="Task Count"
                  placeholder="Enter Task Count"
                  name="task_count"
                  value={formData.task_count}
                  onChange={(e) => handleInputChange(e, index)}
                  textFieldSx={{ width: "220px" }}
                />
                <Tooltip title={index === 0 ? "Cannot delete first item" : "Delete"}>
                  <span>
                    <IconButton
                      onClick={index === 0 ? undefined : () => handleDelete(index)}
                      disabled={index === 0}
                      sx={{
                        marginLeft: "10px",
                        color: "#ec480f",
                        marginTop: "28px",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        })}
        <AddButton onClick={handleAddMore}>Add More</AddButton>
      </form>
    </div>
  );
};

export default VendorAllocationBody;
